// Approval Node Component

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Avatar,
  AvatarGroup
} from '@mui/material';
import {
  HowToVote as ApprovalIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
  Person as PersonIcon
} from '@mui/icons-material';

interface ApprovalNodeData {
  label: string;
  description?: string;
  status?: 'idle' | 'pending' | 'approved' | 'rejected' | 'timeout';
  approvers?: Array<{
    id: string;
    name: string;
    email: string;
    avatar?: string;
    status?: 'pending' | 'approved' | 'rejected';
  }>;
  requiredApprovals?: number;
  currentApprovals?: number;
  message?: string;
  timeout?: number;
  submittedAt?: string;
}

const ApprovalNode: React.FC<NodeProps<ApprovalNodeData>> = ({ 
  data, 
  selected, 
  id 
}) => {
  const getStatusIcon = () => {
    switch (data.status) {
      case 'approved':
        return <ApprovedIcon sx={{ color: '#4CAF50' }} />;
      case 'rejected':
        return <RejectedIcon sx={{ color: '#F44336' }} />;
      case 'pending':
        return <PendingIcon sx={{ color: '#FF9800', animation: 'pulse 2s ease-in-out infinite' }} />;
      default:
        return <ApprovalIcon sx={{ color: '#9C27B0' }} />;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'approved':
        return '#E8F5E9';
      case 'rejected':
        return '#FFEBEE';
      case 'pending':
        return '#FFF3E0';
      default:
        return '#F3E5F5';
    }
  };

  const getBorderColor = () => {
    if (selected) return '#1976D2';
    switch (data.status) {
      case 'approved':
        return '#4CAF50';
      case 'rejected':
        return '#F44336';
      case 'pending':
        return '#FF9800';
      default:
        return '#9C27B0';
    }
  };

  const getApprovalProgress = () => {
    if (!data.currentApprovals || !data.requiredApprovals) return 0;
    return Math.min(100, (data.currentApprovals / data.requiredApprovals) * 100);
  };

  const getPendingApprovers = () => {
    return data.approvers?.filter(approver => approver.status === 'pending') || [];
  };

  const getApprovedCount = () => {
    return data.approvers?.filter(approver => approver.status === 'approved').length || 0;
  };

  const getRejectedCount = () => {
    return data.approvers?.filter(approver => approver.status === 'rejected').length || 0;
  };

  return (
    <Box sx={{ minWidth: 220 }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />
      
      <Card
        sx={{
          backgroundColor: getStatusColor(),
          border: `2px solid ${getBorderColor()}`,
          borderRadius: 3,
          minHeight: 120,
          transition: 'all 0.2s ease',
          boxShadow: selected ? 6 : 2,
          position: 'relative',
          '&:hover': {
            boxShadow: 8,
            transform: 'translateY(-2px)'
          }
        }}
      >
        {/* Pending glow effect */}
        {data.status === 'pending' && (
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              borderRadius: 3,
              border: `2px solid ${getBorderColor()}60`,
              animation: 'glow 2s ease-in-out infinite',
              pointerEvents: 'none'
            }}
          />
        )}

        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          {/* Header with Icon and Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '50%',
                backgroundColor: `${getBorderColor()}20`,
                color: getBorderColor(),
                mr: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {getStatusIcon()}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: 1.2
                }}
              >
                {data.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                Approval Required
              </Typography>
            </Box>
          </Box>

          {/* Message */}
          {data.message && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1.5,
                fontSize: '0.8rem',
                lineHeight: 1.3,
                fontStyle: 'italic'
              }}
            >
              "{data.message}"
            </Typography>
          )}

          {/* Approvers */}
          {data.approvers && data.approvers.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  mb: 1,
                  display: 'block'
                }}
              >
                Approvers ({getApprovedCount()}/{data.approvers.length})
              </Typography>
              <AvatarGroup max={4} sx={{ justifyContent: 'flex-start' }}>
                {data.approvers.map((approver) => (
                  <Avatar
                    key={approver.id}
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: '0.75rem',
                      border: `2px solid ${
                        approver.status === 'approved' 
                          ? '#4CAF50' 
                          : approver.status === 'rejected'
                          ? '#F44336'
                          : '#FF9800'
                      }`,
                      backgroundColor: approver.avatar ? 'transparent' : '#E0E0E0'
                    }}
                    src={approver.avatar}
                    title={`${approver.name} - ${approver.status || 'pending'}`}
                  >
                    {!approver.avatar && (
                      approver.status === 'approved' ? '✓' :
                      approver.status === 'rejected' ? '✗' :
                      <PersonIcon sx={{ fontSize: '0.9rem' }} />
                    )}
                  </Avatar>
                ))}
              </AvatarGroup>
            </Box>
          )}

          {/* Progress and Status */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Chip
              label={data.status || 'idle'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: getBorderColor(),
                color: 'white',
                textTransform: 'uppercase'
              }}
            />
            
            {data.requiredApprovals && (
              <Chip
                label={`${data.currentApprovals || 0}/${data.requiredApprovals} required`}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  borderColor: getBorderColor(),
                  color: getBorderColor()
                }}
              />
            )}
          </Box>

          {/* Approval breakdown */}
          {(getApprovedCount() > 0 || getRejectedCount() > 0) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {getApprovedCount() > 0 && (
                <Chip
                  label={`${getApprovedCount()} approved`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    backgroundColor: '#4CAF5020',
                    color: '#4CAF50'
                  }}
                />
              )}
              {getRejectedCount() > 0 && (
                <Chip
                  label={`${getRejectedCount()} rejected`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    backgroundColor: '#F4433620',
                    color: '#F44336'
                  }}
                />
              )}
            </Box>
          )}

          {/* Timestamp */}
          {data.submittedAt && data.status === 'pending' && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                mt: 1,
                display: 'block'
              }}
            >
              Submitted: {new Date(data.submittedAt).toLocaleString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Output Handles */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="approved"
        style={{
          background: '#4CAF50',
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%',
          left: '30%'
        }}
      />
      
      <Handle
        type="source"
        position={Position.Bottom}
        id="rejected"
        style={{
          background: '#F44336',
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%',
          left: '70%'
        }}
      />

      {/* Labels for outputs */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -25,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          px: 2
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: '#4CAF50',
            fontWeight: 600
          }}
        >
          APPROVED
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: '#F44336',
            fontWeight: 600
          }}
        >
          REJECTED
        </Typography>
      </Box>

      {/* Pending indicator */}
      {data.status === 'pending' && (
        <Box
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: '#FF9800',
            animation: 'bounce 1.5s ease-in-out infinite',
            zIndex: 10
          }}
        />
      )}

      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.8;
            }
          }
          
          @keyframes glow {
            0%, 100% {
              box-shadow: 0 0 5px rgba(255, 152, 0, 0.3);
            }
            50% {
              box-shadow: 0 0 20px rgba(255, 152, 0, 0.6);
            }
          }
          
          @keyframes bounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-4px);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(ApprovalNode);